import { yupResolver } from "@hookform/resolvers/yup";
import { submitInvestorProfileStep3 } from "api/investor";
import { IError } from "api/types";
import Button from "components/InvestorCommon/Button/Button";
import SelectBox from "components/InvestorCommon/SelectV2/Select";
import SourceOfFundsOthersInput from "components/InvestorCommon/SourceOfFundsOthersInput";
import { FC, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { useInvestorClassificationRequirementContext } from "state/investorClassificationRequirement";
import { INACTIVE_CLASSIFICATION_ERROR } from "utils/corporateInvestorClassification";
import {
	createSourceOfFundsOthersYupSchema,
	getActiveIndividualClassificationOptions,
	getIndividualRequirementClassificationOptions,
	getSourceOfFundsSubmitValue,
	IInvestorProfileForClassification,
	IndividualInvestorClassificationsMap,
	isActiveIndividualClassificationId,
	isSelectedInactiveProfileClassification,
	isSourceOfFundsOthers,
	resolveSourceOfFundsFormValues,
} from "utils/individualInvestorClassification";
import { decodeMetadataValue, escapeMetadataValue } from "utils/sanitizeMetadata";
import * as yup from "yup";
import { Actions, FormGrid } from "./Styled";

type IndividualFormValues = {
	investorType: string;
	investorClassificationCriteriaId: number | null;
	sourceOfWealth: string;
	sourceOfFunds: string;
	sourceOfFundsOthers: string;
	incomeLevel: string;
	annualAnticipatedInvestmentAmount: string;
};

export type ClassificationFormProps = {
	onClose: () => void;
	onCompleted: () => void;
	isSubmitting: boolean;
	setIsSubmitting: (loading: boolean) => void;
};

const createIndividualSchema = (
	classifications: IndividualInvestorClassificationsMap,
	profile?: IInvestorProfileForClassification | null,
) =>
	yup.object().shape({
		investorType: yup.string().required("Investor Type is required.").nullable(),
		investorClassificationCriteriaId: yup
			.number()
			.transform((_value, originalValue) => {
				if (originalValue === "" || originalValue === null || originalValue === undefined) {
					return null;
				}
				const parsed = Number(originalValue);
				return Number.isNaN(parsed) ? null : parsed;
			})
			.nullable()
			.required("Investor Classification is required.")
			.test("active-classification", INACTIVE_CLASSIFICATION_ERROR, function (value) {
				if (value == null) return true;

				const selectedInvestorType = this.parent.investorType as string | undefined;
				if (!selectedInvestorType) return true;

				if (isSelectedInactiveProfileClassification(value, profile)) {
					return false;
				}

				const activeOptions = getActiveIndividualClassificationOptions(
					selectedInvestorType,
					classifications,
				);
				if (!activeOptions.length) return true;

				return isActiveIndividualClassificationId(
					value,
					selectedInvestorType,
					classifications,
				);
			}),
		sourceOfWealth: yup.string().required("Source of Wealth is required.").nullable(),
		sourceOfFunds: yup.string().required("Source of Funds is required.").nullable(),
		sourceOfFundsOthers: yup.string().when("sourceOfFunds", {
			is: (value: string) => isSourceOfFundsOthers(value),
			then: createSourceOfFundsOthersYupSchema(),
			otherwise: yup.string().nullable(),
		}),
		incomeLevel: yup.string().required("Income Level is required.").nullable(),
		annualAnticipatedInvestmentAmount: yup
			.string()
			.required("Annual Anticipated Investment Amount is required.")
			.nullable(),
	});

const SELECT_BASED_STYLES = {
	input: (_provided: Record<string, unknown>) => ({
		"input:focus": {
			boxShadow: "none",
		},
	}),
};

const IndividualInvestorClassificationForm: FC<ClassificationFormProps> = ({
	onClose,
	onCompleted,
	isSubmitting,
	setIsSubmitting,
}) => {
	const submitRef = useRef<HTMLButtonElement>(null);
	const { investorProfile, individualInvestorClassifications, individualMetadataOptions } =
		useInvestorClassificationRequirementContext();

	const validationSchema = useMemo(
		() => createIndividualSchema(individualInvestorClassifications, investorProfile),
		[individualInvestorClassifications, investorProfile],
	);
	const previousInvestorTypeRef = useRef<string | undefined>(
		investorProfile?.investorType ?? undefined,
	);
	const sourceOfFundsOptions = individualMetadataOptions.sourceOfFunds;

	const initialFormValues = useMemo((): IndividualFormValues => {
		const sourceOfFundsFields = resolveSourceOfFundsFormValues(
			investorProfile?.sourceOfFunds,
			sourceOfFundsOptions,
		);

		return {
			investorType: investorProfile?.investorType ?? "",
			investorClassificationCriteriaId:
				investorProfile?.investorClassificationCriteriaId != null
					? Number(investorProfile.investorClassificationCriteriaId)
					: null,
			sourceOfWealth: decodeMetadataValue(investorProfile?.sourceOfWealth) ?? "",
			sourceOfFunds: sourceOfFundsFields.sourceOfFunds,
			sourceOfFundsOthers: sourceOfFundsFields.sourceOfFundsOthers,
			incomeLevel: decodeMetadataValue(investorProfile?.incomeLevel) ?? "",
			annualAnticipatedInvestmentAmount:
				decodeMetadataValue(investorProfile?.annualAnticipatedInvestmentAmount) ?? "",
		};
	}, [investorProfile, sourceOfFundsOptions]);

	const {
		handleSubmit,
		register,
		control,
		reset,
		watch,
		setError,
		setValue,
		trigger,
		formState: { errors },
	} = useForm<IndividualFormValues>({
		resolver: yupResolver(validationSchema),
		defaultValues: initialFormValues,
		mode: "onChange",
	});

	const watchedInvestorType = watch("investorType");
	const watchedSourceOfFunds = watch("sourceOfFunds");
	const showSourceOfFundsOthers = isSourceOfFundsOthers(watchedSourceOfFunds);

	const listInvestorClassificationData = useMemo(
		() =>
			getIndividualRequirementClassificationOptions(
				watchedInvestorType,
				individualInvestorClassifications,
				investorProfile,
			),
		[watchedInvestorType, individualInvestorClassifications, investorProfile],
	);

	const investorTypesOptions = individualMetadataOptions?.investorTypes ?? [];

	useEffect(() => {
		if (!investorProfile) return;

		const investorType = investorProfile.investorType ?? "";
		const sourceOfFundsFields = resolveSourceOfFundsFormValues(
			investorProfile.sourceOfFunds,
			sourceOfFundsOptions,
		);

		reset({
			investorType,
			investorClassificationCriteriaId:
				investorProfile.investorClassificationCriteriaId != null
					? Number(investorProfile.investorClassificationCriteriaId)
					: null,
			sourceOfWealth: decodeMetadataValue(investorProfile.sourceOfWealth) ?? "",
			sourceOfFunds: sourceOfFundsFields.sourceOfFunds,
			sourceOfFundsOthers: sourceOfFundsFields.sourceOfFundsOthers,
			incomeLevel: decodeMetadataValue(investorProfile.incomeLevel) ?? "",
			annualAnticipatedInvestmentAmount:
				decodeMetadataValue(investorProfile.annualAnticipatedInvestmentAmount) ?? "",
		});
		previousInvestorTypeRef.current = investorType;

		void trigger("investorClassificationCriteriaId");
	}, [investorProfile, reset, sourceOfFundsOptions, trigger]);

	const handleInvestorTypeChange = (nextInvestorType: string) => {
		const previousInvestorType = previousInvestorTypeRef.current ?? "";
		const normalizedNextInvestorType = nextInvestorType ?? "";

		// Clear on any type change, including "Select" (empty). Otherwise a stale
		// classification id remains, the field looks empty, and validation shows
		// the inactive-classification error instead of required.
		if (previousInvestorType !== normalizedNextInvestorType) {
			setValue("investorClassificationCriteriaId", null, { shouldValidate: true });
		}

		previousInvestorTypeRef.current = normalizedNextInvestorType || undefined;
	};

	const handleSourceOfFundsChange = (nextSourceOfFunds: string) => {
		if (!isSourceOfFundsOthers(nextSourceOfFunds)) {
			setValue("sourceOfFundsOthers", "");
		}
	};

	const [submitRequirement] = useMutation(submitInvestorProfileStep3, {
		onSuccess: () => {
			toast.success("Your changes has been saved successfully!");
			onCompleted();
		},
		onError: (error: IError) => {
			setIsSubmitting(false);
			const message = Array.isArray(error?.message) ? error.message[0] : error?.message;

			if (message === INACTIVE_CLASSIFICATION_ERROR) {
				setError("investorClassificationCriteriaId", {
					type: "server",
					message: INACTIVE_CLASSIFICATION_ERROR,
				});
				return;
			}

			toast.error(message || "Unable to save changes. Please try again.");
		},
	});

	const onSubmit = (values: IndividualFormValues) => {
		setIsSubmitting(true);
		submitRequirement({
			profileComplete: true,
			investorType: values.investorType,
			investorClassificationCriteriaId: values.investorClassificationCriteriaId,
			sourceOfWealth: escapeMetadataValue(values.sourceOfWealth),
			sourceOfFunds: getSourceOfFundsSubmitValue(
				values.sourceOfFunds,
				values.sourceOfFundsOthers,
			),
			incomeLevel: escapeMetadataValue(values.incomeLevel),
			annualAnticipatedInvestmentAmount: escapeMetadataValue(
				values.annualAnticipatedInvestmentAmount,
			),
		});
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FormGrid>
				<SelectBox
					title={
						<>
							Investor Type <span className="text-red-500">*</span>
						</>
					}
					register={register}
					control={control}
					propertiesName="investorType"
					styles={SELECT_BASED_STYLES}
					data={investorTypesOptions}
					errorMessage={errors.investorType?.message}
					handleChangeValue={handleInvestorTypeChange}
				/>
				<SelectBox
					title={
						<>
							Source of Wealth <span className="text-red-500">*</span>
						</>
					}
					register={register}
					control={control}
					propertiesName="sourceOfWealth"
					styles={SELECT_BASED_STYLES}
					data={individualMetadataOptions.sourceOfWealth}
					errorMessage={errors.sourceOfWealth?.message}
				/>
				<SelectBox
					disabled={!watchedInvestorType}
					title={
						<>
							Investor Classification <span className="text-red-500">*</span>
						</>
					}
					placeholder={
						watchedInvestorType
							? "Select one or more options"
							: "Select Investor Type first"
					}
					register={register}
					control={control}
					propertiesName="investorClassificationCriteriaId"
					styles={SELECT_BASED_STYLES}
					data={listInvestorClassificationData}
					errorMessage={
						!watchedInvestorType
							? "Select Investor Type first"
							: errors.investorClassificationCriteriaId?.message
					}
				/>
				<div className="flex w-full flex-col">
					<SelectBox
						title={
							<>
								Source of Funds <span className="text-red-500">*</span>
							</>
						}
						register={register}
						control={control}
						propertiesName="sourceOfFunds"
						styles={SELECT_BASED_STYLES}
						data={sourceOfFundsOptions}
						errorMessage={errors.sourceOfFunds?.message}
						handleChangeValue={handleSourceOfFundsChange}
					/>
					{showSourceOfFundsOthers && (
						<SourceOfFundsOthersInput
							{...register("sourceOfFundsOthers")}
							value={watch("sourceOfFundsOthers") ?? ""}
							errorMessage={errors.sourceOfFundsOthers?.message}
						/>
					)}
				</div>
				<SelectBox
					title={
						<>
							Annual Anticipated Investment Amount{" "}
							<span className="text-red-500">*</span>
						</>
					}
					register={register}
					control={control}
					propertiesName="annualAnticipatedInvestmentAmount"
					styles={SELECT_BASED_STYLES}
					data={individualMetadataOptions.annualAnticipatedInvestmentAmount}
					errorMessage={errors.annualAnticipatedInvestmentAmount?.message}
				/>
				<SelectBox
					title={
						<>
							Income Level <span className="text-red-500">*</span>
						</>
					}
					register={register}
					control={control}
					propertiesName="incomeLevel"
					styles={SELECT_BASED_STYLES}
					data={individualMetadataOptions.incomeLevel}
					errorMessage={errors.incomeLevel?.message}
				/>
			</FormGrid>

			<Actions>
				<Button
					type="button"
					theme="primary"
					size="small"
					disabled={isSubmitting}
					onClick={onClose}
				>
					Cancel
				</Button>
				<Button
					ref={submitRef}
					type="submit"
					theme="secondary"
					size="small"
					disabled={isSubmitting}
				>
					Submit
				</Button>
			</Actions>
		</form>
	);
};

export default IndividualInvestorClassificationForm;
